import { useId } from 'react'
import { SegmentedControl } from '../admin/pickers.js'
import { Checkbox, SwitchRow } from '../admin/form.js'
import { CANAL_LIBELLE, VERROU_LIBELLE, type Canal } from './soirees-maison-model.js'
import {
  saisonDeLaValeur, nouvelleDateHeure, valeurChampDateHeure,
  type ChampTarif, type ErreursTarif, type SaisieTarif,
} from './tarif-saisie-model.js'

/**
 * LES CHAMPS D'UN TARIF — un seul jeu, pour l'assistant (création) et la
 * fiche (ajout, modification). Aucun bouton « Enregistrer » ici : la page ou
 * la modale qui les porte décide (Save Bar, ou étape de l'assistant).
 *
 * Ce qui ne se change plus après le premier billet (`verrous`) reste AFFICHÉ,
 * grisé, avec sa raison — jamais masqué en silence. Les champs sans objet
 * (frais d'un tarif sur place, exonération en ligne) ne s'affichent pas.
 */

const CANAUX_OPTIONS: { value: Canal; label: string }[] = [
  { value: 'SUR_PLACE', label: CANAL_LIBELLE.SUR_PLACE },
  { value: 'EN_LIGNE', label: CANAL_LIBELLE.EN_LIGNE },
  { value: 'INVITATION', label: CANAL_LIBELLE.INVITATION },
]

const VERROU_DU_CHAMP: Partial<Record<ChampTarif, string>> = {
  prix: 'PRIX', frais: 'FRAIS', canal: 'CANAL', exoneration: 'EXONERATION', tva: 'TVA',
}

function Champ(props: { id: string; label: string; aide?: string | null | undefined; erreur?: string | undefined; verrou?: string | null | undefined; children: React.ReactNode }) {
  return (
    <div className={`lc-field bs-champ${props.erreur ? ' bs-champ--erreur' : ''}`}>
      <label className="lc-label" htmlFor={props.id}>{props.label}</label>
      {props.children}
      {props.erreur ? <span className="lc-helper lc-helper--error" role="alert">{props.erreur}</span>
        : props.verrou ? <span className="lc-helper bs-champ__verrou">{props.verrou}</span>
          : props.aide ? <span className="lc-helper">{props.aide}</span> : null}
    </div>
  )
}

export function TarifChamps(props: {
  valeur: SaisieTarif
  onChange: (s: SaisieTarif) => void
  mode: 'creation' | 'modification'
  salles: readonly { code: string; nom: string }[]
  erreurs?: ErreursTarif | undefined
  verrous?: readonly string[] | undefined
  /** La raison d'un verrou quand celle du tableau serait fausse (caisse de téléphone ouverte, billet émis) : `raisonsVerrousTarif`. */
  raisonsVerrous?: Readonly<Record<string, string>> | undefined
  /** « 5,5 % » — le taux que prendra un tarif créé sans TVA. */
  tvaDefaut?: string | null | undefined
  /** D'où vient une heure de vente calculée (« 02:30 dans la nuit »), tant que personne n'y touche. */
  origineHeures?: { vente_debut?: string | null; vente_fin?: string | null } | undefined
  quotaMin?: number | null | undefined
  /** Une modification sur une soirée en ligne : l'ordre et la description se règlent aussi. */
  complet?: boolean | undefined
  lectureSeule?: boolean | undefined
}) {
  const base = useId()
  const v = props.valeur
  const e = props.erreurs ?? {}
  const verrous = props.verrous ?? []
  const raison = (code: string): string => props.raisonsVerrous?.[code] ?? VERROU_LIBELLE[code] ?? 'Ce champ ne se change plus.'
  const fige = (c: ChampTarif): string | null => {
    const code = VERROU_DU_CHAMP[c]
    return code && verrous.includes(code) ? raison(code) : null
  }
  const ro = props.lectureSeule === true
  const set = <K extends ChampTarif>(k: K, x: SaisieTarif[K]) => props.onChange({ ...v, [k]: x })
  const changerCanal = (c: Canal) => {
    // Un tarif sur place ou une invitation n'a pas de frais ; l'exonération ne se fait que sur place.
    props.onChange({ ...v, canal: c, frais: c === 'EN_LIGNE' ? v.frais : '', exoneration: c === 'SUR_PLACE' ? v.exoneration : false })
  }
  const id = (k: string) => `${base}-${k}`

  const dateHeure = (k: 'vente_debut' | 'vente_fin', label: string) => {
    const saison = saisonDeLaValeur(v[k])
    const origine = props.origineHeures?.[k]
    return (
      <Champ id={id(k)} label={label} erreur={e[k]}
        aide={saison ? `Heure ${saison === 'hiver' ? 'd’hiver' : 'd’été'} : cette heure existe deux fois cette nuit-là.` : origine ? `Proposée par le modèle : ${origine}.` : 'Heure de Paris. Vide : aucune.'}>
        <span className="bs-champ__ligne">
          <input id={id(k)} className="lc-input" type="datetime-local" step={60} disabled={ro}
            value={valeurChampDateHeure(v[k])}
            onChange={(ev) => set(k, nouvelleDateHeure(v[k], ev.target.value))} />
          {v[k] && !ro ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => set(k, '')}>Effacer</button> : null}
        </span>
      </Champ>
    )
  }

  return (
    <div className="bs-tarif-champs">
      <Champ id={id('nom')} label="Nom du tarif" erreur={e.nom} aide="Tel qu’il s’affiche sur le billet et à la caisse.">
        <input id={id('nom')} className="lc-input" value={v.nom} maxLength={80} disabled={ro} onChange={(ev) => set('nom', ev.target.value)} />
      </Champ>

      <div className="lc-field bs-champ">
        <span className="lc-label">Canal</span>
        {fige('canal') || ro ? <span className="lc-pill lc-pill--idle">{CANAL_LIBELLE[v.canal]}</span>
          : <SegmentedControl value={v.canal} options={CANAUX_OPTIONS} onChange={changerCanal} ariaLabel="Canal de vente" />}
        {e.canal ? <span className="lc-helper lc-helper--error" role="alert">{e.canal}</span>
          : fige('canal') ? <span className="lc-helper bs-champ__verrou">{fige('canal')}</span>
            : <span className="lc-helper">{v.canal === 'SUR_PLACE' ? 'Vendu à la caisse de la porte.' : v.canal === 'EN_LIGNE' ? 'Pendant la démo, rien ne vend en ligne.' : 'Gratuit ; aucun écran n’émet encore d’invitation.'}</span>}
      </div>

      <div className="bs-grille">
        <Champ id={id('prix')} label="Prix (€)" erreur={e.prix} verrou={fige('prix')} aide={v.canal === 'INVITATION' ? 'Une invitation est gratuite : 0.' : 'Toutes taxes comprises.'}>
          <input id={id('prix')} className="lc-input" inputMode="decimal" placeholder="à saisir" value={v.prix} disabled={ro || !!fige('prix')} onChange={(ev) => set('prix', ev.target.value)} />
        </Champ>
        {v.canal === 'EN_LIGNE' ? (
          <Champ id={id('frais')} label="Frais de service (€)" erreur={e.frais} verrou={fige('frais')} aide="Vide : aucun.">
            <input id={id('frais')} className="lc-input" inputMode="decimal" value={v.frais} disabled={ro || !!fige('frais')} onChange={(ev) => set('frais', ev.target.value)} />
          </Champ>
        ) : null}
        <Champ id={id('tva')} label="TVA (%)" erreur={e.tva} verrou={fige('tva')}
          aide={props.mode === 'creation' ? `Vide : le réglage de la billetterie${props.tvaDefaut ? ` (${props.tvaDefaut})` : ''}. Figée au premier billet.` : 'Figée au premier billet émis.'}>
          <input id={id('tva')} className="lc-input" inputMode="decimal" placeholder={props.mode === 'creation' ? (props.tvaDefaut ?? 'réglage par défaut') : ''} value={v.tva} disabled={ro || !!fige('tva')} onChange={(ev) => set('tva', ev.target.value)} />
        </Champ>
        <Champ id={id('quota')} label="Quota (billets)" erreur={e.quota}
          aide={typeof props.quotaMin === 'number' && props.quotaMin > 0 ? `Pas moins que les ${props.quotaMin} billet(s) déjà vendu(s). Vide : sans quota.` : 'Vide : sans quota.'}>
          <input id={id('quota')} className="lc-input" inputMode="numeric" placeholder="sans quota" value={v.quota} disabled={ro} onChange={(ev) => set('quota', ev.target.value)} />
        </Champ>
      </div>

      <div className="bs-grille">
        <Champ id={id('groupe')} label="Cascade : groupe" erreur={e.cascade_groupe} aide="Les tarifs d’un même groupe s’ouvrent l’un après l’autre.">
          <input id={id('groupe')} className="lc-input" placeholder="sur-place" value={v.cascade_groupe} maxLength={32} disabled={ro} onChange={(ev) => set('cascade_groupe', ev.target.value)} />
        </Champ>
        <Champ id={id('rang')} label="Cascade : rang" erreur={e.cascade_rang} aide="1 s’ouvre d’abord. Échanger deux rangs passe par un rang libre.">
          <input id={id('rang')} className="lc-input" inputMode="numeric" value={v.cascade_rang} disabled={ro} onChange={(ev) => set('cascade_rang', ev.target.value)} />
        </Champ>
      </div>

      <div className="bs-grille">
        {dateHeure('vente_debut', 'Début de vente')}
        {dateHeure('vente_fin', 'Fin de vente')}
        <Champ id={id('entree')} label="Heure limite d’entrée" erreur={e.entree_avant}
          aide={verrous.includes('ENTREE_AVANT_AVANCER') ? VERROU_LIBELLE['ENTREE_AVANT_AVANCER'] : v.canal === 'SUR_PLACE' ? 'Sans effet sur place : utilisez la fin de vente.' : 'Heure de la nuit : « 01:30 » tombe le lendemain. Vide : aucune.'}>
          <span className="bs-champ__ligne">
            <input id={id('entree')} className="lc-input" type="time" step={60} value={v.entree_avant} disabled={ro} onChange={(ev) => set('entree_avant', ev.target.value)} />
            {v.entree_avant && !ro ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => set('entree_avant', '')}>Effacer</button> : null}
          </span>
        </Champ>
      </div>

      {props.salles.length > 1 ? (
        <div className="lc-field bs-champ">
          <span className="lc-label">Salles ouvertes à ce tarif</span>
          <span className="bs-cases">
            {props.salles.map((s) => (
              <Checkbox key={s.code} label={s.nom} checked={v.salles.includes(s.code)} disabled={ro}
                onChange={(on) => set('salles', on ? [...v.salles, s.code] : v.salles.filter((x) => x !== s.code))} />
            ))}
          </span>
          {e.salles ? <span className="lc-helper lc-helper--error" role="alert">{e.salles}</span>
            : <span className="lc-helper">{verrous.includes('SALLES_RETRAIT') ? raison('SALLES_RETRAIT') : 'Aucune cochée : toutes les salles de la soirée. Affichées à la porte, pas contrôlées.'}</span>}
        </div>
      ) : null}

      {v.canal === 'SUR_PLACE' ? (
        <div className={`bs-champ${e.exoneration ? ' bs-champ--erreur' : ''}`}>
          <SwitchRow checked={v.exoneration} onChange={(x) => set('exoneration', x)} disabled={ro || !!fige('exoneration')}
            title="Exonération" desc={fige('exoneration') ?? 'Entrée gratuite, motif demandé à la caisse. Toujours à 0 €, sans frais.'} />
          {e.exoneration ? <span className="lc-helper lc-helper--error" role="alert">{e.exoneration}</span> : null}
        </div>
      ) : null}
      <SwitchRow checked={v.cache} onChange={(x) => set('cache', x)} disabled={ro}
        title="Caché" desc="Enregistré, mais rien ne le lit encore." />

      <Champ id={id('consigne')} label="Consigne pour la porte" erreur={e.consigne_porte} aide="80 caractères. Affichée au scan d’un billet, jamais pour une vente à la caisse.">
        <input id={id('consigne')} className="lc-input" value={v.consigne_porte} maxLength={80} disabled={ro} placeholder="Vérifier la carte étudiante" onChange={(ev) => set('consigne_porte', ev.target.value)} />
      </Champ>

      {props.complet ? (
        <div className="bs-grille">
          <Champ id={id('ordre')} label="Ordre d’affichage" erreur={e.ordre} aide="De 0 à 1 000 : le plus petit s’affiche en premier.">
            <input id={id('ordre')} className="lc-input" inputMode="numeric" value={v.ordre} disabled={ro} onChange={(ev) => set('ordre', ev.target.value)} />
          </Champ>
          <Champ id={id('desc')} label="Description" erreur={e.description} aide="500 caractères au plus.">
            <textarea id={id('desc')} className="lc-input bs-zone" rows={2} value={v.description} maxLength={500} disabled={ro} onChange={(ev) => set('description', ev.target.value)} />
          </Champ>
        </div>
      ) : null}
    </div>
  )
}
